import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApp, api } from '@/context/AppContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  ArrowRight, ArrowLeft, Sparkles, Users, BookOpen, BarChart3,
  ClipboardCheck, Accessibility, GraduationCap, Heart, Building2,
  Image as ImageIcon, Mic, FileText, Award, Trophy, Star, ShieldCheck, Globe2, Zap, MessageCircle
} from 'lucide-react';

export default function Landing() {
  const { t, lang } = useApp();
  const [stats, setStats] = useState(null);
  const ArrowIcon = lang === 'ar' ? ArrowLeft : ArrowRight;

  useEffect(() => {
    api.get('/curriculum/stats').then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const features = [
    { icon: Sparkles, color: 'from-purple-500/15 to-purple-500/5 text-purple-700', t: 'f_ai_title', d: 'f_ai_desc' },
    { icon: ClipboardCheck, color: 'from-indigo-500/15 to-indigo-500/5 text-indigo-700', t: 'f_iep_title', d: 'f_iep_desc' },
    { icon: BookOpen, color: 'from-emerald-500/15 to-emerald-500/5 text-emerald-700', t: 'f_curriculum_title', d: 'f_curriculum_desc' },
    { icon: BarChart3, color: 'from-sky-500/15 to-sky-500/5 text-sky-700', t: 'f_analytics_title', d: 'f_analytics_desc' },
    { icon: GraduationCap, color: 'from-amber-500/15 to-amber-500/5 text-amber-700', t: 'f_assess_title', d: 'f_assess_desc' },
    { icon: Accessibility, color: 'from-rose-500/15 to-rose-500/5 text-rose-700', t: 'f_a11y_title', d: 'f_a11y_desc' },
  ];

  const portals = [
    { icon: GraduationCap, t: 'portal_teacher', d: 'portal_teacher_desc', to: '/teacher', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    { icon: Heart, t: 'portal_student', d: 'portal_student_desc', to: '/student', color: 'bg-rose-50 text-rose-700 border-rose-200' },
    { icon: Users, t: 'portal_parent', d: 'portal_parent_desc', to: '/parent', color: 'bg-amber-50 text-amber-700 border-amber-200' },
    { icon: Building2, t: 'portal_admin', d: 'portal_admin_desc', to: '/admin', color: 'bg-sky-50 text-sky-700 border-sky-200' },
  ];

  const aiCapabilities = [
    { icon: FileText, label: lang === 'ar' ? 'دروس' : 'Lessons' },
    { icon: ImageIcon, label: lang === 'ar' ? 'صور' : 'Images' },
    { icon: Mic, label: lang === 'ar' ? 'صوت' : 'Voice' },
    { icon: BookOpen, label: lang === 'ar' ? 'قصص' : 'Stories' },
    { icon: Trophy, label: lang === 'ar' ? 'شهادات' : 'Certificates' },
    { icon: ClipboardCheck, label: lang === 'ar' ? 'تقييمات' : 'Rubrics' },
    { icon: MessageCircle, label: lang === 'ar' ? 'كوتش' : 'Coach' },
    { icon: Award, label: lang === 'ar' ? 'ألعاب' : 'Games' },
  ];

  return (
    <main id="main" data-testid="landing-page">
      {/* HERO */}
      <section className="relative bg-zayed-pattern overflow-hidden">
        {/* Decorative blobs */}
        <div className="absolute top-20 -end-20 w-80 h-80 rounded-full bg-accent/10 blur-3xl pointer-events-none" aria-hidden="true" />
        <div className="absolute bottom-10 -start-20 w-96 h-96 rounded-full bg-primary/10 blur-3xl pointer-events-none" aria-hidden="true" />
        <div className="absolute top-1/2 start-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] dot-pattern opacity-30 pointer-events-none" aria-hidden="true" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 lg:py-28 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-primary/20 shadow-sm text-primary text-xs font-bold mb-6">
              <span className="w-2 h-2 rounded-full bg-accent animate-pulse" aria-hidden="true" />
              {lang === 'ar' ? 'منصة همم للتربية الرياضية المعدّلة — ٢٠٢٦' : 'Himam Platform — 2026'}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black tracking-tight leading-[1.02] text-zayed-ink mb-6">
              {t('hero_title')}
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed mb-8">
              {t('hero_subtitle')}
            </p>
            <div className="flex flex-wrap gap-3 mb-10">
              <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-base h-12 px-7 shadow-lg shadow-primary/20" data-testid="hero-cta-start">
                <Link to="/register">{t('cta_start')} <ArrowIcon className="w-4 h-4 ms-2" aria-hidden="true" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-base h-12 px-7 border-2" data-testid="hero-cta-explore">
                <Link to="/curriculum">{t('cta_explore')}</Link>
              </Button>
            </div>

            {/* AI capability strip */}
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                {lang === 'ar' ? 'مدعوم بـ AI' : 'Powered by AI'}
              </span>
              <div className="flex flex-wrap gap-2">
                {aiCapabilities.map((c) => {
                  const Icon = c.icon;
                  return (
                    <span key={c.label} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-border text-xs font-medium shadow-sm">
                      <Icon className="w-3 h-3 text-primary" aria-hidden="true" />
                      {c.label}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Hero image stack */}
          <div className="lg:col-span-5 relative">
            <div className="relative animate-float-soft">
              <div className="absolute -inset-4 bg-gradient-to-br from-primary/15 via-accent/10 to-transparent rounded-3xl blur-2xl" aria-hidden="true" />
              <img
                src="https://images.pexels.com/photos/8617765/pexels-photo-8617765.jpeg"
                alt={lang === 'ar' ? 'طلاب في فصل دراسي شامل' : 'Students in an inclusive classroom'}
                className="relative rounded-3xl shadow-2xl border-[5px] border-white object-cover aspect-[4/5] w-full"
                loading="lazy"
              />
            </div>

            {/* Floating stat cards */}
            <div className="absolute -bottom-6 -start-6 bg-white rounded-2xl p-4 shadow-2xl border border-border max-w-[220px] animate-float-soft" style={{ animationDelay: '0.5s' }}>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-lg bg-primary/10 grid place-items-center"><BookOpen className="w-4 h-4 text-primary" /></div>
                <div className="text-xs text-muted-foreground font-semibold">{t('stats_goals')}</div>
              </div>
              <div className="text-3xl font-black text-primary stat-number">{stats?.total_goals ?? '—'}</div>
            </div>

            <div className="absolute -top-6 -end-4 bg-white rounded-2xl p-4 shadow-2xl border border-border max-w-[220px] animate-float-soft" style={{ animationDelay: '1s' }}>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-lg bg-accent/10 grid place-items-center"><Sparkles className="w-4 h-4 text-accent" /></div>
                <div className="text-xs text-muted-foreground font-semibold">{lang === 'ar' ? 'مولّدات AI' : 'AI Generators'}</div>
              </div>
              <div className="text-3xl font-black text-accent stat-number">16</div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS BAR */}
      <section className="border-y border-border bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { k: 'stats_goals', v: stats?.total_goals, icon: BookOpen },
            { k: 'stats_disabilities', v: stats?.disability_type_count, icon: Heart },
            { k: 'stats_ages', v: stats?.age_group_count, icon: Users },
            { k: 'stats_sports', v: stats?.sport_count, icon: Trophy },
          ].map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.k} className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 grid place-items-center shrink-0">
                  <Icon className="w-6 h-6 text-primary" aria-hidden="true" />
                </div>
                <div className="text-start">
                  <div className="text-3xl sm:text-4xl font-black text-primary leading-none stat-number">{s.v ?? '—'}</div>
                  <div className="text-xs text-muted-foreground mt-1 font-semibold">{t(s.k)}</div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* AI STUDIO SPOTLIGHT */}
      <section className="py-16 sm:py-24 bg-gradient-to-b from-white to-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <Badge className="bg-primary/10 text-primary border-primary/20 mb-4">{lang === 'ar' ? 'الجوهرة' : 'Flagship'}</Badge>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-4">
                {lang === 'ar' ? 'استوديو ذكاء اصطناعي متخصص في التربية البدنية' : 'AI Studio for Adapted PE'}
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                {lang === 'ar'
                  ? '١٦ مولّداً ذكياً — قصص مصوّرة، خطط دروس، أنشطة معدّلة لـ١٠ فئات إعاقة، روبركس، شهادات، كوتش افتراضي بالصوت العربي. كل المخرجات حصراً في الصحة واللياقة والرياضة لأصحاب الهمم.'
                  : '16 specialized generators — illustrated stories, lesson plans, adapted activities for 10 disability categories, rubrics, certificates, Arabic-voice virtual coach. All outputs strictly in PE, health & fitness for People of Determination.'}
              </p>
              <Button asChild size="lg" className="bg-primary hover:bg-primary/90" data-testid="ai-spotlight-cta">
                <Link to="/ai-studio"><Sparkles className="w-4 h-4 me-2" />{lang === 'ar' ? 'افتح الاستوديو' : 'Open Studio'}</Link>
              </Button>
            </div>

            <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: lang === 'ar' ? 'قصص' : 'Stories', icon: BookOpen, color: 'bg-purple-100 text-purple-700' },
                { label: lang === 'ar' ? 'بطاقات' : 'Flashcards', icon: Award, color: 'bg-amber-100 text-amber-700' },
                { label: lang === 'ar' ? 'خطط دروس' : 'Lessons', icon: FileText, color: 'bg-emerald-100 text-emerald-700' },
                { label: lang === 'ar' ? 'أنشطة' : 'Activities', icon: Zap, color: 'bg-sky-100 text-sky-700' },
                { label: lang === 'ar' ? 'تقييمات' : 'Rubrics', icon: ClipboardCheck, color: 'bg-teal-100 text-teal-700' },
                { label: lang === 'ar' ? 'IEP' : 'IEP', icon: ClipboardCheck, color: 'bg-indigo-100 text-indigo-700' },
                { label: lang === 'ar' ? 'شهادات' : 'Certs', icon: Trophy, color: 'bg-yellow-100 text-yellow-700' },
                { label: lang === 'ar' ? 'الكوتش' : 'Coach', icon: MessageCircle, color: 'bg-cyan-100 text-cyan-700' },
              ].map((it, i) => {
                const Icon = it.icon;
                return (
                  <div key={i} className="aspect-square border border-border bg-white rounded-2xl p-4 flex flex-col items-start justify-between card-elevate" data-testid={`ai-card-${i}`}>
                    <div className={`w-11 h-11 rounded-xl ${it.color} grid place-items-center`}>
                      <Icon className="w-5 h-5" aria-hidden="true" />
                    </div>
                    <div className="font-bold text-sm">{it.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-16 sm:py-24" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <Badge className="bg-secondary text-foreground/70 mb-3">{lang === 'ar' ? 'منظومة شاملة' : 'Complete platform'}</Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-4">{t('features_title')}</h2>
            <p className="text-muted-foreground text-lg">{t('features_subtitle')}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f) => {
              const Icon = f.icon;
              return (
                <Card key={f.t} className="card-elevate border-border bg-white overflow-hidden group">
                  <CardContent className="p-6">
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${f.color} grid place-items-center mb-5 group-hover:scale-110 transition-transform`}>
                      <Icon className="w-7 h-7" aria-hidden="true" />
                    </div>
                    <h3 className="font-bold text-lg mb-2">{t(f.t)}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{t(f.d)}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* PORTALS */}
      <section className="py-16 sm:py-24 bg-secondary/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-3">{t('portals_title')}</h2>
            <p className="text-muted-foreground">{lang === 'ar' ? 'تجربة مصمّمة لكل دور بدقة' : 'A purpose-built experience for every role'}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {portals.map((p) => {
              const Icon = p.icon;
              return (
                <Link key={p.to} to={p.to} data-testid={`portal-link-${p.to.slice(1)}`} className="card-elevate group block bg-white rounded-2xl p-6 border border-border">
                  <div className={`w-14 h-14 rounded-2xl ${p.color} border grid place-items-center mb-4 group-hover:scale-110 transition-transform`}>
                    <Icon className="w-7 h-7" aria-hidden="true" />
                  </div>
                  <h3 className="font-bold text-lg mb-2 group-hover:text-primary transition-colors">{t(p.t)}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{t(p.d)}</p>
                  <div className="mt-4 text-primary text-sm font-bold inline-flex items-center gap-1">
                    {lang === 'ar' ? 'دخول' : 'Enter'} <ArrowIcon className="w-3 h-3" aria-hidden="true" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* TRUST / VALUE BAND */}
      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid sm:grid-cols-3 gap-4">
          {[
            { icon: ShieldCheck, t: lang === 'ar' ? 'وصول WCAG AAA' : 'WCAG AAA Accessible', d: lang === 'ar' ? 'تباين عالي، تحكم بالخط، تنقل بلوحة المفاتيح، قارئ شاشة' : 'High contrast, font controls, keyboard nav, screen reader' },
            { icon: Globe2, t: lang === 'ar' ? 'ثنائي اللغة كامل' : 'Fully Bilingual', d: lang === 'ar' ? 'عربي RTL + إنجليزي LTR في كل صفحة وكل مخرج AI' : 'Arabic RTL + English LTR everywhere, including AI output' },
            { icon: Star, t: lang === 'ar' ? 'مبني على المنهج الرسمي' : 'Built on Official Curriculum', d: lang === 'ar' ? '٣٬٠٩٦ هدفاً تعليمياً معدّلاً جاهزاً للاستخدام' : '3,096 adapted learning goals ready to use' },
          ].map((it, i) => {
            const Icon = it.icon;
            return (
              <div key={i} className="flex gap-4 p-5 rounded-2xl bg-white border border-border card-elevate">
                <div className="w-12 h-12 rounded-xl bg-primary/10 grid place-items-center shrink-0">
                  <Icon className="w-6 h-6 text-primary" aria-hidden="true" />
                </div>
                <div>
                  <h3 className="font-bold text-base mb-1">{it.t}</h3>
                  <p className="text-sm text-muted-foreground">{it.d}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-16 sm:py-24 bg-primary text-primary-foreground relative overflow-hidden">
        <div className="absolute inset-0 dot-pattern opacity-10" aria-hidden="true" />
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black mb-4">{lang === 'ar' ? 'ابدأ رحلتك مع أول منصة من نوعها' : 'Start with the first platform of its kind'}</h2>
          <p className="text-primary-foreground/80 mb-8 text-base sm:text-lg max-w-2xl mx-auto">{t('hero_subtitle')}</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button asChild size="lg" className="bg-accent hover:bg-accent/90 text-accent-foreground h-12 px-8 font-bold" data-testid="bottom-cta">
              <Link to="/register">{t('cta_start')}</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="bg-transparent border-2 border-white/40 text-white hover:bg-white/10 h-12 px-8">
              <Link to="/curriculum">{t('cta_explore')}</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-border bg-white py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary text-primary-foreground grid place-items-center font-black">ز</div>
            <div>
              <div className="font-bold text-foreground">{t('brand')}</div>
              <div className="text-xs">{t('brand_sub')}</div>
            </div>
          </div>
          <div className="text-xs">© 2026 · {t('footer_rights')}</div>
        </div>
      </footer>
    </main>
  );
}
